import { Table, Tabs, Typography } from 'antd';
import i18n from 'i18next';
import { FC, memo, useState } from 'react';

import { ColumnTypes, ProjectPaymentSchedule } from 'models';

import { useTranslate } from 'hooks';

import Utils from 'utils/utils';
import { PaymentStatus } from 'utils/constants';

import { TableItemStatus } from 'componets/ui';
import CustomTabBar from "../../../../../../componets/ui/CustomTabBar";

const { t } = i18n;

const columns = [
	{
		title: '',
		dataIndex: 'paymentNumber',
	},
	{
		title: t('paymentScheduledDateText'),
		dataIndex: 'paymentDate',
		render: (v: string) => <span className="nobr">{v}</span>
	},
	{
		title: t('principalPaymentText'),
		dataIndex: 'principalAmount',
		render: (amount: number) => {
			return Utils.moneyFormat(amount);
		},
	},
	{
		title: t('interestPaymentText'),
		dataIndex: 'interestAmount',
		render: (amount: number) => {
			return Utils.moneyFormat(amount);
		},
	},
	{
		title: t('actualPaymentDateText'),
		dataIndex: 'factualPaymentDate',
	},
	{
		title: t('paymentStatusText'),
		dataIndex: 'status',
		render: (status: string) => {
			return (
				<TableItemStatus
					status={status}
					isRed={status === PaymentStatus.LATE || status === PaymentStatus.PAY_WITH_DELAY}
					isGreen={status === PaymentStatus.PAID}
				/>
			);
		},
	},
];

export const PrimaryItemPaymentSchedule: FC<{
	data: ProjectPaymentSchedule[];
	archive: ProjectPaymentSchedule[][];
	notes: Record<string, any>[];
}> = memo(({ data, archive, notes = [] }) => {
	const {
		paymentScheduleText, activeTextSchedules, versionText, loanHasBeenRepaidEarly
	} = useTranslate([
		'paymentScheduleText',
		'activeTextSchedules',
		'versionText',
		'loanHasBeenRepaidEarly',
	]);

	const [type, setType] = useState('active');

	const reversedArchive = Utils.reverseMatrix(archive);

	const foundCancelledItem = data.find((el) => el.status === PaymentStatus.CANCELED);
	return (
		<div className="primaryItem__block">
			<Typography.Title className="primaryItem__blockTitle" level={4}>
				{paymentScheduleText}
			</Typography.Title>

			{archive.length > 0 ? (
				<Tabs
					className="accountTabs"
					type="card"
					defaultActiveKey="active"
					activeKey={type}
					onChange={(v: string) => setType(v)}
					destroyInactiveTabPane
					renderTabBar={(props) => {
						return <CustomTabBar
							panes={props.panes}
							onTabClick={props.onTabClick}
							activeKey={props.activeKey}
							expandButtonText={archive.length > 1 ? t('previousSchedulesText') : t('previousScheduleText')}
						/>;
					}}
				>
					<Tabs.TabPane tab={activeTextSchedules} key="active">
						<ActiveSchedule data={data} />
					</Tabs.TabPane>

					{reversedArchive.map((item, index) => {
						return (
							<Tabs.TabPane tab={`${versionText} ${item[0]?.version}`} key={index}>
								<Table
									className="accountTable accountTable__paymentSchedule"
									columns={columns as ColumnTypes}
									dataSource={item}
									pagination={false}
									scroll={{ x: 670 }}
								/>
							</Tabs.TabPane>
						);
					})}
				</Tabs>
			) : (
				<ActiveSchedule data={data} />
			)}
			{foundCancelledItem && <p className="project_note">{loanHasBeenRepaidEarly}</p>}
		</div>
	);
});

export const ActiveSchedule: FC<{ data: ProjectPaymentSchedule[] }> = ({ data }) => {
	const { noPaymentScheduleText } = useTranslate(['noPaymentScheduleText']);
	const _cols = columns.map((el) => {
		if (el.dataIndex === 'status') {
			return {
				title: t('paymentStatusText'),
				dataIndex: 'status',
				render: (status: string) => {
					return (
						<TableItemStatus
							status={status === PaymentStatus.CANCELED ? `${t('PAID')}*` : status}
							isRed={status === PaymentStatus.LATE || status === PaymentStatus.PAY_WITH_DELAY}
							isGreen={status === PaymentStatus.PAID}
							isBlue={status === PaymentStatus.CANCELED}
						/>
					);
				},
			};
		}
		return el;
	});
	return (
		<>
			{data.length > 0 ? (
				<Table
					className="accountTable accountTable__paymentSchedule"
					columns={_cols as ColumnTypes}
					dataSource={data}
					pagination={false}
					scroll={{ x: 670 }}
				/>
			) : (
				<Typography.Text className="primaryItem__text">{noPaymentScheduleText}</Typography.Text>
			)}
		</>
	);
};
